import { useState } from 'react';
import { useAuthStore } from '../store/authStore.js';
import { LockIcon, PersonIcon } from '../components/Icons.jsx';

export default function Login() {
  const login = useAuthStore((s) => s.login);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      setError('Пожалуйста, введите логин');
      return;
    }
    if (!password.trim()) {
      setError('Пожалуйста, введите пароль');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await login(username.trim(), password.trim());
    } catch (err) {
      setError(err.message || 'Неверный логин или пароль');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-between bg-[#f4f6f9] text-[#191e24] px-4 py-8 sm:py-12 animate-fade-in">
      {/* Top Header */}
      <div className="max-w-md w-full mx-auto flex items-center justify-between pb-6 border-b border-slate-200/80">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[#002882] text-white flex items-center justify-center shadow-sm">
            <LockIcon size={20} />
          </div>
          <div>
            <div className="text-base font-bold text-slate-900 tracking-tight font-sans">
              Видеосервис
            </div>
            <div className="text-[11px] text-slate-500 font-medium -mt-0.5">
              Вход в защищенный контур
            </div>
          </div>
        </div>

        <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#f0f4fc] text-[#002882] border border-[#d1dcf0]">
          Безопасный вход
        </span>
      </div>

      {/* Main Login Card */}
      <div className="max-w-md w-full mx-auto my-auto py-8">
        <div className="bg-white rounded-3xl border border-slate-200/90 shadow-xl shadow-[#002882]/5 p-6 sm:p-10 space-y-6">
          <div className="text-center space-y-2">
            <div className="w-14 h-14 rounded-2xl bg-[#f0f4fc] text-[#002882] flex items-center justify-center mx-auto ring-8 ring-[#f0f4fc]/50">
              <LockIcon size={26} />
            </div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
              Доступ к сервису
            </h1>
            <p className="text-xs sm:text-sm text-slate-500">
              Введите учетные данные для работы с видеоконсультациями и комнатами
            </p>
          </div>

          {error && (
            <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs sm:text-sm font-medium flex items-center gap-2 animate-fade-in">
              <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                Логин
              </label>
              <div className="relative">
                <PersonIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Введите логин…"
                  required
                  autoFocus
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#f8fafc] border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-[#002882] focus:ring-2 focus:ring-[#002882]/10 transition-all"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Пароль
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="text-[11px] font-medium text-[#002882] hover:underline"
                >
                  {showPassword ? 'Скрыть' : 'Показать'}
                </button>
              </div>
              <div className="relative">
                <LockIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Введите пароль…"
                  required
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#f8fafc] border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-[#002882] focus:ring-2 focus:ring-[#002882]/10 transition-all"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-4 rounded-xl bg-[#002882] hover:bg-[#001f66] text-white font-bold text-sm shadow-md shadow-[#002882]/20 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  <span>Проверка доступа…</span>
                </>
              ) : (
                <span>Войти в сервис</span>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Footer Info */}
      <div className="max-w-md w-full mx-auto text-center text-xs text-slate-400 pt-4 flex flex-col items-center gap-1.5">
        <div className="text-[11px] text-slate-400">
          Защищенное соединение • Доступ ограничен
        </div>
      </div>
    </div>
  );
}
